/**
 * 经纪人物业支持记录抽屉。
 * 这些记录用于留存团队替地产方做的可行性、初步规划和预算支持，不属于屋主 CRM 跟进；
 * 因此按物业独立归档，并用 CrmDetailDrawer 的更深层级展示，返回时保留经纪人和物业上下文。
 */
import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Paperclip, Upload, X } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { CrmDetailDrawer } from "../crm/CrmDetailDrawer";
import AttachmentLightbox from "../AttachmentLightbox";
import { fileUrl, kindOf } from "../../lib/attach";
import { uploadChunked, UPLOAD_MAX } from "../../lib/upload";
import { fmtDT } from "./tmConst";

const supportPath = (realtorId, propertyId) => `/tm/db/realtors/${realtorId}/properties/${propertyId}/support`;

/**
 * 展示或新增一条物业支持记录。
 * @param {object} props api/zh 为页面服务；realtorId/propertyId 固定记录归属；supportId 有值时只读展示，
 *   为空时进入新建；canEdit 继承经纪人资料库现有编辑权；level 决定多抽屉层级；onSaved 通知物业列表刷新。
 * @returns {JSX.Element} 第 2 层记录抽屉，以及仅预览图片时显示的全屏预览。
 */
export function TMRealtorSupportDrawer({ api, zh, realtorId, propertyId, supportId, canEdit, onClose, onSaved, level = 2 }) {
  const [record, setRecord] = useState(null);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileRef = useRef(null);
  const readOnly = !!supportId;
  const imageAttachments = (record?.attachments || []).filter((item) => (item.contentType || "").startsWith("image/"));
  const closeDrawer = () => {
    // 图片全屏预览在抽屉之上；先关预览，避免一次 Escape 同时卸载支持记录抽屉。
    if (lightboxIndex !== null) { setLightboxIndex(null); return; }
    onClose?.();
  };

  useEffect(() => {
    let alive = true;
    if (!supportId) return () => { alive = false; };
    api.get(`${supportPath(realtorId, propertyId)}/${supportId}`)
      .then(({ data }) => { if (alive) setRecord(data); })
      .catch((error) => {
        toast.error(error?.response?.data?.detail || (zh ? "加载支持记录失败" : "Could not load support record"));
        if (alive) onClose?.();
      });
    return () => { alive = false; };
  }, [api, realtorId, propertyId, supportId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 复用全站分片上传；先上传到文件服务，提交记录时再由后端校验并绑定物业范围。 */
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (attachments.length + files.length > 20) {
      toast.error(zh ? "一条记录最多添加 20 个图片或视频" : "A record can contain up to 20 photos or videos");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const kind = kindOf(file.name, file.type);
        if (kind !== "image" && kind !== "video") {
          throw new Error(zh ? "这里只能添加图片或视频" : "Only images and videos are supported");
        }
        if (file.size > UPLOAD_MAX) throw new Error(zh ? "单个文件不能超过 200 MB" : "Each file must be 200 MB or smaller");
        const uploaded = await uploadChunked(api, file, { onPct: setUploadPct });
        setAttachments((current) => [...current, {
          fileId: uploaded.fileId,
          filename: uploaded.filename || file.name,
          contentType: uploaded.contentType || file.type,
          kind: uploaded.kind || kind,
        }]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || (zh ? "上传失败" : "Upload failed"));
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /** 记录必须有文字或媒体；后端补充作者、时间和物业归属后，发布内容作为历史记录只读保留。 */
  const save = async () => {
    if (!text.trim() && !attachments.length) {
      toast.error(zh ? "请填写支持记录，或至少添加一个图片/视频" : "Add text or at least one photo/video");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(supportPath(realtorId, propertyId), { text: text.trim(), attachments });
      toast.success(zh ? "支持记录已保存" : "Support record saved");
      onSaved?.(data);
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || (zh ? "保存支持记录失败" : "Could not save support record"));
    } finally {
      setBusy(false);
    }
  };

  const title = readOnly
    ? (record?.createdAt ? `${zh ? "支持记录" : "Support record"} · ${fmtDT(record.createdAt)}` : (zh ? "支持记录" : "Support record"))
    : (zh ? "新增支持记录" : "New support record");

  return (
    <>
      <CrmDetailDrawer title={title} onClose={closeDrawer} testId="tm-realtor-support-drawer" level={level}>
        {readOnly ? (
          !record ? <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
            <article className="space-y-4" data-testid="tm-realtor-support-detail">
              <header className="border-b border-slate-200 pb-3">
                <div className="text-xs text-slate-500">{record.authorName || record.authorUsername || "—"} · {fmtDT(record.createdAt)}</div>
                <h2 className="mt-1 text-base font-semibold text-slate-800">{zh ? "物业支持记录" : "Property support record"}</h2>
              </header>
              {record.text && <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{record.text}</p>}
              {(record.attachments || []).length > 0 && (
                <section className="space-y-2" aria-label={zh ? "记录附件" : "Record attachments"}>
                  <h3 className="text-xs font-semibold text-slate-600">{zh ? "图片 / 视频" : "Photos / videos"}</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(record.attachments || []).map((item) => {
                      const isImage = (item.contentType || "").startsWith("image/");
                      const imageIndex = imageAttachments.findIndex((image) => image.fileId === item.fileId);
                      return isImage ? (
                        <button key={item.fileId} type="button" onClick={() => setLightboxIndex(imageIndex)} className="group overflow-hidden rounded-lg border border-slate-200 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" data-testid={`tm-realtor-support-image-${item.fileId}`}>
                          <img src={fileUrl(item.fileId)} alt={item.filename || (zh ? "支持记录图片" : "Support record image")} loading="lazy" className="h-52 w-full bg-slate-50 object-contain" />
                          <span className="block truncate px-2 py-1.5 text-xs text-slate-600 group-hover:text-brand-700">{item.filename}</span>
                        </button>
                      ) : (
                        <figure key={item.fileId} className="overflow-hidden rounded-lg border border-slate-200 bg-slate-950" data-testid={`tm-realtor-support-video-${item.fileId}`}>
                          <video src={fileUrl(item.fileId)} controls playsInline preload="metadata" className="max-h-72 w-full" aria-label={item.filename || (zh ? "支持记录视频" : "Support record video")} />
                          <figcaption className="truncate bg-white px-2 py-1.5 text-xs text-slate-600">{item.filename}</figcaption>
                        </figure>
                      );
                    })}
                  </div>
                </section>
              )}
            </article>
          )
        ) : (
          <section className="space-y-4" data-testid="tm-realtor-support-compose">
            <div>
              <h2 className="text-base font-semibold text-slate-800">{zh ? "记录本次物业支持" : "Record property support"}</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">{zh ? "可写可行性分析、初步规划、预算评估或和地产方沟通的内容。记录归属当前物业，不会创建 CRM 屋主线索。" : "Record feasibility, planning, budget review, or realtor discussions. This stays with the property and does not create a CRM homeowner lead."}</p>
            </div>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-600">{zh ? "支持内容" : "Support details"}</span>
              <textarea value={text} onChange={(event) => setText(event.target.value)} rows={8} maxLength={12000} placeholder={zh ? "自由填写本次支持内容……" : "Write the support details…"} data-testid="tm-realtor-support-text" className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" disabled={!canEdit || busy || uploading} />
              <span className="block text-right text-[11px] text-slate-400">{text.length}/12000</span>
            </label>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="tm-realtor-support-file-input" onChange={(event) => uploadFiles(event.target.files)} />
                <Button type="button" size="sm" variant="outline" disabled={!canEdit || busy || uploading || attachments.length >= 20} onClick={() => fileRef.current?.click()} data-testid="tm-realtor-support-upload">
                  {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                  {uploading ? `${zh ? "上传中" : "Uploading"} ${uploadPct}%` : (zh ? "添加图片 / 视频" : "Add photos / videos")}
                </Button>
                <span className="text-[11px] text-slate-500">{attachments.length}/20 · {zh ? "单个文件最多 200 MB" : "Up to 200 MB each"}</span>
              </div>
              {attachments.length > 0 && <div className="flex flex-wrap gap-2">
                {attachments.map((item, index) => <button type="button" key={`${item.fileId}-${index}`} disabled={uploading || busy} onClick={() => setAttachments((current) => current.filter((_, i) => i !== index))} className="inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50" title={zh ? "点击移除附件" : "Click to remove attachment"} data-testid={`tm-realtor-support-pending-${index}`}>
                  {item.kind === "video" ? <Paperclip className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  <span className="max-w-48 truncate">{item.filename}</span><X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>)}
              </div>}
            </div>
            <div className="flex justify-end border-t border-slate-200 pt-3">
              <Button type="button" onClick={save} disabled={!canEdit || busy || uploading} data-testid="tm-realtor-support-save" className="bg-gradient-to-r from-[#4F6BFF] to-[#7B5CFF] text-white">
                {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{zh ? "保存记录" : "Save record"}
              </Button>
            </div>
          </section>
        )}
      </CrmDetailDrawer>
      {lightboxIndex !== null && imageAttachments[lightboxIndex] && <AttachmentLightbox items={imageAttachments} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndex={setLightboxIndex} urlOf={fileUrl} zh={zh} />}
    </>
  );
}

export default TMRealtorSupportDrawer;
